import React from 'react';
import { Settings } from 'lucide-react';

import { UpdatePlanConfigRequest } from '../../types/firm';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';

type PlanConfigFormProps = {
  formData: UpdatePlanConfigRequest;
  isPending: boolean;
  isError: boolean;
  error: Error | null;
  successMessage: string | null;
  onUpdateNestedValue: (path: string[], value: any) => void;
  getNestedValue: (path: string[]) => any;
  onSubmit: (e: React.FormEvent) => void;
  onCancel: () => void;
};

export function PlanConfigForm({
  formData,
  isPending,
  isError,
  error,
  successMessage,
  onUpdateNestedValue,
  getNestedValue,
  onSubmit,
  onCancel,
}: PlanConfigFormProps) {
  return (
    <div className="border-stroke-primary">
      <div className="mb-4 flex items-center justify-between">
        <h3 className="flex items-center text-lg font-semibold text-text-primary">
          <Settings className="mr-2 h-5 w-5" />
          Plan Configuration
        </h3>
      </div>

      <form onSubmit={onSubmit} className="space-y-6">
        {/* Plan Type */}
        <div className="rounded-lg bg-surface-secondary p-4">
          <Label htmlFor="plan" className="mb-2 block text-sm font-medium">
            Plan Type
          </Label>
          <select
            id="plan"
            value={formData.plan || ''}
            onChange={(e) => onUpdateNestedValue(['plan'], e.target.value || undefined)}
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
          >
            <option value="">Select plan</option>
            <option value="free">Free</option>
            <option value="full">Full</option>
            <option value="custom">Custom</option>
          </select>
        </div>

        {/* Deal Syndication */}
        <div className="rounded-lg bg-surface-secondary p-4">
          <h4 className="mb-3 text-sm font-semibold text-text-primary">Deal Syndication</h4>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <div>
              <Label htmlFor="deal_syndication_total" className="mb-2 block text-xs">
                Total
              </Label>
              <Input
                id="deal_syndication_total"
                type="number"
                value={getNestedValue(['deal_syndication', 'total']) || ''}
                onChange={(e) =>
                  onUpdateNestedValue(
                    ['deal_syndication', 'total'],
                    e.target.value ? Number(e.target.value) : undefined,
                  )
                }
                placeholder="Total"
              />
            </div>
            <div>
              <Label htmlFor="deal_syndication_active" className="mb-2 block text-xs">
                Active
              </Label>
              <Input
                id="deal_syndication_active"
                type="number"
                value={getNestedValue(['deal_syndication', 'active']) || ''}
                onChange={(e) =>
                  onUpdateNestedValue(
                    ['deal_syndication', 'active'],
                    e.target.value ? Number(e.target.value) : undefined,
                  )
                }
                placeholder="Active"
              />
            </div>
            <div>
              <Label htmlFor="deal_syndication_quick_deals" className="mb-2 block text-xs">
                Quick Deals
              </Label>
              <Input
                id="deal_syndication_quick_deals"
                type="number"
                value={getNestedValue(['deal_syndication', 'quick_deals']) || ''}
                onChange={(e) =>
                  onUpdateNestedValue(
                    ['deal_syndication', 'quick_deals'],
                    e.target.value ? Number(e.target.value) : undefined,
                  )
                }
                placeholder="Quick Deals"
              />
            </div>
          </div>
        </div>

        {/* Seats */}
        <div className="rounded-lg bg-surface-secondary p-4">
          <h4 className="mb-3 text-sm font-semibold text-text-primary">Seats</h4>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <div>
              <Label htmlFor="seats_admin" className="mb-2 block text-xs">
                Admin
              </Label>
              <Input
                id="seats_admin"
                type="number"
                value={getNestedValue(['seats', 'admin']) || ''}
                onChange={(e) =>
                  onUpdateNestedValue(['seats', 'admin'], e.target.value ? Number(e.target.value) : undefined)
                }
                placeholder="Admin seats"
              />
            </div>
            <div>
              <Label htmlFor="seats_member" className="mb-2 block text-xs">
                Member
              </Label>
              <Input
                id="seats_member"
                type="number"
                value={getNestedValue(['seats', 'member']) || ''}
                onChange={(e) =>
                  onUpdateNestedValue(['seats', 'member'], e.target.value ? Number(e.target.value) : undefined)
                }
                placeholder="Member seats"
              />
            </div>
            <div>
              <Label htmlFor="seats_co_manager" className="mb-2 block text-xs">
                Co-Manager
              </Label>
              <Input
                id="seats_co_manager"
                type="number"
                value={getNestedValue(['seats', 'co_manager']) || ''}
                onChange={(e) =>
                  onUpdateNestedValue(
                    ['seats', 'co_manager'],
                    e.target.value ? Number(e.target.value) : undefined,
                  )
                }
                placeholder="Co-Manager seats"
              />
            </div>
            <div>
              <Label htmlFor="seats_partner" className="mb-2 block text-xs">
                Partner
              </Label>
              <Input
                id="seats_partner"
                type="number"
                value={getNestedValue(['seats', 'partner']) || ''}
                onChange={(e) =>
                  onUpdateNestedValue(['seats', 'partner'], e.target.value ? Number(e.target.value) : undefined)
                }
                placeholder="Partner seats"
              />
            </div>
            <div>
              <Label htmlFor="seats_investor" className="mb-2 block text-xs">
                Investor
              </Label>
              <Input
                id="seats_investor"
                type="number"
                value={getNestedValue(['seats', 'investor']) || ''}
                onChange={(e) =>
                  onUpdateNestedValue(['seats', 'investor'], e.target.value ? Number(e.target.value) : undefined)
                }
                placeholder="Investor seats"
              />
            </div>
          </div>
        </div>

        {/* Team Collaboration */}
        <div className="rounded-lg bg-surface-secondary p-4">
          <h4 className="mb-3 text-sm font-semibold text-text-primary">Team Collaboration</h4>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <Label htmlFor="team_collaboration_co_managers" className="mb-2 block text-xs">
                Co-Managers
              </Label>
              <Input
                id="team_collaboration_co_managers"
                type="number"
                value={getNestedValue(['team_collaboration', 'co_managers']) || ''}
                onChange={(e) =>
                  onUpdateNestedValue(
                    ['team_collaboration', 'co_managers'],
                    e.target.value ? Number(e.target.value) : undefined,
                  )
                }
                placeholder="Co-Managers"
              />
            </div>
            <div>
              <Label htmlFor="team_collaboration_partners" className="mb-2 block text-xs">
                Partners
              </Label>
              <Input
                id="team_collaboration_partners"
                type="number"
                value={getNestedValue(['team_collaboration', 'partners']) || ''}
                onChange={(e) =>
                  onUpdateNestedValue(
                    ['team_collaboration', 'partners'],
                    e.target.value ? Number(e.target.value) : undefined,
                  )
                }
                placeholder="Partners"
              />
            </div>
          </div>
        </div>

        {/* Data Room */}
        <div className="rounded-lg bg-surface-secondary p-4">
          <h4 className="mb-3 text-sm font-semibold text-text-primary">Data Room</h4>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <div>
              <Label htmlFor="data_room_storage_per_deal" className="mb-2 block text-xs">
                Storage per Deal
              </Label>
              <Input
                id="data_room_storage_per_deal"
                type="number"
                value={getNestedValue(['data_room', 'storage_per_deal']) || ''}
                onChange={(e) =>
                  onUpdateNestedValue(
                    ['data_room', 'storage_per_deal'],
                    e.target.value ? Number(e.target.value) : undefined,
                  )
                }
                placeholder="Storage per deal"
              />
            </div>
            <div>
              <Label htmlFor="data_room_documents_per_deal" className="mb-2 block text-xs">
                Documents per Deal
              </Label>
              <Input
                id="data_room_documents_per_deal"
                type="number"
                value={getNestedValue(['data_room', 'documents_per_deal']) || ''}
                onChange={(e) =>
                  onUpdateNestedValue(
                    ['data_room', 'documents_per_deal'],
                    e.target.value ? Number(e.target.value) : undefined,
                  )
                }
                placeholder="Documents per deal"
              />
            </div>
            <div>
              <Label htmlFor="data_room_storage_notify_threshold" className="mb-2 block text-xs">
                Storage Notify Threshold
              </Label>
              <Input
                id="data_room_storage_notify_threshold"
                type="number"
                value={getNestedValue(['data_room', 'storage_notify_threshold']) || ''}
                onChange={(e) =>
                  onUpdateNestedValue(
                    ['data_room', 'storage_notify_threshold'],
                    e.target.value ? Number(e.target.value) : undefined,
                  )
                }
                placeholder="Storage notify threshold"
              />
            </div>
          </div>
        </div>

        {/* LP Management */}
        <div className="rounded-lg bg-surface-secondary p-4">
          <h4 className="mb-3 text-sm font-semibold text-text-primary">LP Management</h4>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <div>
              <Label htmlFor="lp_management_limit" className="mb-2 block text-xs">
                Limit
              </Label>
              <Input
                id="lp_management_limit"
                type="number"
                value={getNestedValue(['lp_management', 'limit']) || ''}
                onChange={(e) =>
                  onUpdateNestedValue(
                    ['lp_management', 'limit'],
                    e.target.value ? Number(e.target.value) : undefined,
                  )
                }
                placeholder="Limit"
              />
            </div>
            <div>
              <Label htmlFor="lp_management_deal_links" className="mb-2 block text-xs">
                Deal Links
              </Label>
              <Input
                id="lp_management_deal_links"
                type="number"
                value={getNestedValue(['lp_management', 'deal_links']) || ''}
                onChange={(e) =>
                  onUpdateNestedValue(
                    ['lp_management', 'deal_links'],
                    e.target.value ? Number(e.target.value) : undefined,
                  )
                }
                placeholder="Deal links"
              />
            </div>
            <div>
              <Label htmlFor="lp_management_ai_recommendation_result" className="mb-2 block text-xs">
                AI Recommendation Result
              </Label>
              <Input
                id="lp_management_ai_recommendation_result"
                type="number"
                value={getNestedValue(['lp_management', 'ai_recommendation_result']) || ''}
                onChange={(e) =>
                  onUpdateNestedValue(
                    ['lp_management', 'ai_recommendation_result'],
                    e.target.value ? Number(e.target.value) : undefined,
                  )
                }
                placeholder="AI recommendation result"
              />
            </div>
          </div>
        </div>

        {/* AI Configuration */}
        <div className="rounded-lg bg-surface-secondary p-4">
          <h4 className="mb-3 text-sm font-semibold text-text-primary">AI Configuration</h4>
          <div className="space-y-4">
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="ai_data_room_assistant"
                checked={getNestedValue(['ai', 'data_room_assistant']) || false}
                onChange={(e) =>
                  onUpdateNestedValue(['ai', 'data_room_assistant'], e.target.checked || undefined)
                }
                className="h-4 w-4 rounded border-gray-300"
              />
              <Label htmlFor="ai_data_room_assistant" className="text-sm">
                Data Room Assistant
              </Label>
            </div>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <Label htmlFor="ai_query_credits_period" className="mb-2 block text-xs">
                  Query Credits Period
                </Label>
                <select
                  id="ai_query_credits_period"
                  value={getNestedValue(['ai', 'query_credits', 'period']) || ''}
                  onChange={(e) =>
                    onUpdateNestedValue(['ai', 'query_credits', 'period'], e.target.value || undefined)
                  }
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                >
                  <option value="">Select period</option>
                  <option value="monthly">Monthly</option>
                  <option value="yearly">Yearly</option>
                </select>
              </div>
              <div>
                <Label htmlFor="ai_query_credits_amount" className="mb-2 block text-xs">
                  Query Credits Amount
                </Label>
                <Input
                  id="ai_query_credits_amount"
                  type="number"
                  value={getNestedValue(['ai', 'query_credits', 'amount']) || ''}
                  onChange={(e) =>
                    onUpdateNestedValue(
                      ['ai', 'query_credits', 'amount'],
                      e.target.value ? Number(e.target.value) : undefined,
                    )
                  }
                  placeholder="Query credits amount"
                />
              </div>
            </div>
          </div>
        </div>

        {/* CQDB Configuration */}
        <div className="rounded-lg bg-surface-secondary p-4">
          <h4 className="mb-3 text-sm font-semibold text-text-primary">CQDB Configuration</h4>
          <div className="space-y-4">
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="cqdb_asset_manager_db_access"
                checked={getNestedValue(['cqdb', 'asset_manager_db_access']) || false}
                onChange={(e) =>
                  onUpdateNestedValue(['cqdb', 'asset_manager_db_access'], e.target.checked || undefined)
                }
                className="h-4 w-4 rounded border-gray-300"
              />
              <Label htmlFor="cqdb_asset_manager_db_access" className="text-sm">
                Asset Manager DB Access
              </Label>
            </div>
            <div>
              <Label htmlFor="cqdb_search_result_display" className="mb-2 block text-xs">
                Search Result Display
              </Label>
              <Input
                id="cqdb_search_result_display"
                type="number"
                value={getNestedValue(['cqdb', 'search_result_display']) || ''}
                onChange={(e) =>
                  onUpdateNestedValue(
                    ['cqdb', 'search_result_display'],
                    e.target.value ? Number(e.target.value) : undefined,
                  )
                }
                placeholder="Search result display"
              />
            </div>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <Label htmlFor="cqdb_email_reveal_limit_period" className="mb-2 block text-xs">
                  Email Reveal Limit Period
                </Label>
                <select
                  id="cqdb_email_reveal_limit_period"
                  value={getNestedValue(['cqdb', 'email_reveal', 'limit', 'period']) || ''}
                  onChange={(e) =>
                    onUpdateNestedValue(
                      ['cqdb', 'email_reveal', 'limit', 'period'],
                      e.target.value || undefined,
                    )
                  }
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                >
                  <option value="">Select period</option>
                  <option value="monthly">Monthly</option>
                  <option value="yearly">Yearly</option>
                </select>
              </div>
              <div>
                <Label htmlFor="cqdb_email_reveal_limit_amount" className="mb-2 block text-xs">
                  Email Reveal Limit Amount
                </Label>
                <Input
                  id="cqdb_email_reveal_limit_amount"
                  type="number"
                  value={getNestedValue(['cqdb', 'email_reveal', 'limit', 'amount']) || ''}
                  onChange={(e) =>
                    onUpdateNestedValue(
                      ['cqdb', 'email_reveal', 'limit', 'amount'],
                      e.target.value ? Number(e.target.value) : undefined,
                    )
                  }
                  placeholder="Email reveal limit amount"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Compliance */}
        <div className="rounded-lg bg-surface-secondary p-4">
          <h4 className="mb-3 text-sm font-semibold text-text-primary">Compliance</h4>
          <div className="space-y-4">
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="compliance_nda_enabled"
                checked={getNestedValue(['compliance', 'nda', 'enabled']) || false}
                onChange={(e) =>
                  onUpdateNestedValue(['compliance', 'nda', 'enabled'], e.target.checked || undefined)
                }
                className="h-4 w-4 rounded border-gray-300"
              />
              <Label htmlFor="compliance_nda_enabled" className="text-sm">
                NDA Enabled
              </Label>
            </div>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <Label htmlFor="compliance_nda_signatures_period" className="mb-2 block text-xs">
                  NDA Signatures Period
                </Label>
                <select
                  id="compliance_nda_signatures_period"
                  value={getNestedValue(['compliance', 'nda', 'signatures', 'period']) || ''}
                  onChange={(e) =>
                    onUpdateNestedValue(
                      ['compliance', 'nda', 'signatures', 'period'],
                      e.target.value || undefined,
                    )
                  }
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                >
                  <option value="">Select period</option>
                  <option value="monthly">Monthly</option>
                  <option value="yearly">Yearly</option>
                </select>
              </div>
              <div>
                <Label htmlFor="compliance_nda_signatures_amount" className="mb-2 block text-xs">
                  NDA Signatures Amount
                </Label>
                <Input
                  id="compliance_nda_signatures_amount"
                  type="number"
                  value={getNestedValue(['compliance', 'nda', 'signatures', 'amount']) || ''}
                  onChange={(e) =>
                    onUpdateNestedValue(
                      ['compliance', 'nda', 'signatures', 'amount'],
                      e.target.value ? Number(e.target.value) : undefined,
                    )
                  }
                  placeholder="NDA signatures amount"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Success/Error Messages */}
        {successMessage && (
          <div className="rounded-md bg-green-50 p-3 text-sm text-green-800">{successMessage}</div>
        )}
        {isError && (
          <div className="rounded-md bg-red-50 p-3 text-sm text-red-800">
            {error instanceof Error ? error.message : 'Failed to update plan configuration. Please try again.'}
          </div>
        )}

        {/* Form Actions */}
        <div className="flex justify-end space-x-2 border-t border-stroke-primary pt-4">
          <Button type="button" variant="outline" onClick={onCancel} disabled={isPending}>
            Cancel
          </Button>
          <Button type="submit" disabled={isPending}>
            {isPending ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </form>
    </div>
  );
}

